import { useCallback, useEffect, useRef, useState } from 'react';
import {
  baselineDays,
  fetchDayStats,
  readStoredBaseline,
  storeBaseline,
  type Baseline,
  type DayStats,
} from '../../lib/dayStats';
import { shiftDateOnly, todayInputValue } from '../../lib/taskDates';
import { useDayBoundary } from '../../lib/useDayBoundary';

/**
 * Today's numbers, and the day they are being measured against.
 *
 * Two windows on two separate effects, deliberately. Today's is keyed on the
 * date and a reload counter and never on the baseline, so moving the card's
 * toggle cannot make the three numbers the reader is looking at blink; only the
 * three small deltas swap. It also means a baseline that fails to load costs the
 * deltas rather than the card.
 *
 * Lifted to the page rather than owned by the card, unlike DailyChecklistCard --
 * and for the reason that card's comment names as the thing that would change
 * it. Something *does* write into these numbers from outside: ticking a task
 * done in the focus or day card moves `tasksCompleted`, and a counter that
 * ignores the click that should have moved it is worse than no counter.
 */
export function useDayStats() {
  const [day, setDay] = useState(todayInputValue);
  const [baseline, setBaselineState] = useState(readStoredBaseline);

  const [today, setToday] = useState<DayStats | null>(null);
  const [base, setBase] = useState<DayStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Why the deltas are missing, for their tooltip. Never routed through
   *  `loadError`: the big numbers are fine and must stay on screen. */
  const [baselineError, setBaselineError] = useState<string | null>(null);

  const [reloads, setReloads] = useState(0);

  const baseDay = shiftDateOnly(day, -baselineDays(baseline));

  // Today. Its own generation counter, so two loads in flight cannot land out
  // of order -- the same guard useDailyChecklist and useDayTasks both use.
  const todayGeneration = useRef(0);
  useEffect(() => {
    const controller = new AbortController();
    const sent = (todayGeneration.current += 1);

    fetchDayStats(day, controller.signal)
      .then((stats) => {
        if (todayGeneration.current !== sent) return;
        setToday(stats);
        setLoadError(null);
      })
      .catch((requestError: unknown) => {
        if (controller.signal.aborted || todayGeneration.current !== sent) return;
        setLoadError(
          requestError instanceof Error ? requestError.message : "Could not load today's numbers.",
        );
      })
      .finally(() => {
        if (!controller.signal.aborted && todayGeneration.current === sent) setLoading(false);
      });

    return () => controller.abort();
  }, [day, reloads]);

  // The baseline, on its own clock.
  const baseGeneration = useRef(0);
  useEffect(() => {
    const controller = new AbortController();
    const sent = (baseGeneration.current += 1);
    // Cleared rather than left showing the previous baseline's numbers, which
    // would render deltas against a day the toggle no longer names.
    setBase(null);

    fetchDayStats(baseDay, controller.signal)
      .then((stats) => {
        if (baseGeneration.current !== sent) return;
        setBase(stats);
        setBaselineError(null);
      })
      .catch((requestError: unknown) => {
        if (controller.signal.aborted || baseGeneration.current !== sent) return;
        setBaselineError(
          requestError instanceof Error ? requestError.message : 'Could not load that day.',
        );
      });

    return () => controller.abort();
    // Deliberately not keyed on `reloads`. That counter means "something
    // changed today", and a past day is a past day -- re-asking for it on every
    // tick would be a request per click, and worse, the `setBase(null)` above
    // would blink all three deltas to em-dashes each time.
  }, [baseDay]);

  // Anchored to today, always -- so unlike the checklist card's stepper there is
  // no "stay put" branch to guard. One setDay re-keys both effects.
  useDayBoundary(useCallback((now: string) => setDay(now), []));

  const setBaseline = useCallback((next: Baseline) => {
    setBaselineState(next);
    storeBaseline(next);
  }, []);

  /**
   * Re-read today, because something outside this card changed one of its
   * numbers.
   *
   * Debounced, because walking a task up through three statuses is three calls
   * inside a second and the count only has to be right once the clicking stops
   * -- but only just. The number sits directly above the list it counts, so the
   * gap between ticking a line and seeing the stat follow is the whole
   * impression this card makes; long enough to notice reads as broken. 150ms
   * coalesces a burst of clicks without being felt on a single one.
   */
  const debounce = useRef(0);
  const refresh = useCallback(() => {
    window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(() => setReloads((current) => current + 1), 150);
  }, []);
  useEffect(() => () => window.clearTimeout(debounce.current), []);

  return {
    baseline,
    setBaseline,
    today,
    base,
    loading,
    loadError,
    baselineError,
    refresh,
  };
}

export type DayStatsState = ReturnType<typeof useDayStats>;
